import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { fehlerText } from "@/lib/fehler";
import {
  Loader2, Check, X, MapPin, Users, Clock, MessageCircle, Phone, FileText, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v) => `${(Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Ornament = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-3 ${className}`}>
    <span className="w-16 h-px bg-[#BA6D52]/50" />
    <span className="w-2 h-2 rotate-45 border border-[#BA6D52]" />
    <span className="w-16 h-px bg-[#BA6D52]/50" />
  </div>
);

export default function AngebotSeite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", funktion: "" });
  const [grund, setGrund] = useState("");
  const [modus, setModus] = useState("");       // "annehmen" | "ablehnen"
  const [busy, setBusy] = useState(false);
  const [notizen, setNotizen] = useState({});   // Änderungswünsche je Gang
  const [wunschName, setWunschName] = useState("");
  const [allgemein, setAllgemein] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/angebotsseiten/${slug}`);
        setData(d);
      } catch {
        setError("Dieses Angebot gibt es nicht — bitte prüfen Sie den Link.");
      }
    })();
  }, [slug]);

  const annehmen = async () => {
    if (form.name.trim().length < 2) { toast.error("Bitte Ihren Namen angeben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/angebotsseiten/${slug}/annehmen`, form);
      const { data: d } = await axios.get(`${API}/angebotsseiten/${slug}`);
      setData(d);
      setModus("");
      toast.success("Vielen Dank — der Termin ist fest eingeplant.");
    } catch (err) {
      toast.error(fehlerText(err, "Das hat nicht funktioniert"));
    } finally { setBusy(false); }
  };

  const wuenscheSenden = async () => {
    const hat = Object.values(notizen).some((v) => (v || "").trim()) || allgemein.trim();
    if (!hat) { toast.error("Bitte mindestens eine Anmerkung schreiben"); return; }
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/angebotsseiten/${slug}/rueckmeldung`, {
        name: wunschName, allgemein, notizen,
      });
      setData(d);
      setNotizen({});
      setAllgemein("");
      setModus("");
      toast.success("Danke — Ihre Anmerkungen sind bei Lukas Stutz angekommen.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(fehlerText(err, "Das hat nicht funktioniert"));
    } finally { setBusy(false); }
  };

  const ablehnen = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/angebotsseiten/${slug}/ablehnen`, { grund });
      const { data: d } = await axios.get(`${API}/angebotsseiten/${slug}`);
      setData(d);
      setModus("");
      toast.success("Danke für die Rückmeldung.");
    } catch (err) {
      toast.error(fehlerText(err, "Das hat nicht funktioniert"));
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center px-6">
        <p className="font-serif text-2xl text-[#2B2320] text-center" data-testid="angebot-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={30} />
      </div>
    );
  }

  const s = data.summen || {};
  const istStunden = (data.art || "menue") === "stunden";
  const istTagessatz = (data.art || "menue") === "tagessatz";
  const stundenText = String(s.stunden_pro_tag ?? "").replace(".", ",");
  const entscheidbar = !["angenommen", "abgelehnt"].includes(data.status) && !data.abgelaufen;
  const wuenscheMoeglich = entscheidbar && !istStunden && !istTagessatz;
  const FIELD = "mt-2 w-full bg-white border border-[#E8DCCB] focus:outline-none focus:border-[#BA6D52] px-4 py-3 text-sm text-[#2B2320] transition-colors";
  const LBL = "text-[11px] uppercase tracking-[0.2em] text-[#6E5B4C]";

  return (
    <div data-testid="page-angebot" className="min-h-screen bg-[#FDF8F1] text-[#2B2320]">
      {/* Kopf */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/anrichten-sauce.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(20,18,14,0.74)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-b from-transparent to-[#FDF8F1]" />
        <div className="relative max-w-3xl mx-auto px-6 pt-10 pb-24 text-center">
          <img src="/logo.png" alt="Lukas Stutz"
            className="h-16 w-16 md:h-20 md:w-20 mx-auto rounded-full bg-[#FAF8F3]/90 p-1.5" />
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#E2C4B2] mt-4">
            Lukas Stutz · Mietkoch für Gastronomie &amp; Events
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#FFFBF4] mt-10 drop-shadow-[0_3px_16px_rgba(0,0,0,0.9)]"
            data-testid="angebot-anrede">
            {data.anrede ? `Guten Tag ${data.anrede},` : "Guten Tag,"}
          </h1>
          <p className="text-base md:text-lg text-[#F2E7DA] mt-6 font-light leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]"
            data-testid="angebot-gruss">
            {data.gruss || (istStunden
              ? `das ist mein festgelegter Stundensatz für Ihren Einsatz ${data.termin_art === "tag" ? `am ${data.datum_lang}` : `${data.termin_text}`}.
                 Bitte einmal kurz bestätigen — den Rest übernehme ich.`
              : istTagessatz
              ? `das ist mein Tagessatz für ${data.termin_art === "tag" ? `den ${data.datum_lang}` : `die Einsatztage ${data.termin_text}`}.
                 Pro Tag sind bis zu ${stundenText} Stunden enthalten — dauert es länger, geht das zu meinen Lasten.
                 Bitte einmal kurz bestätigen.`
              : `hier Ihr persönliches Angebot${data.anlass ? ` für ${data.anlass}` : ""}. 
              Alles Wichtige auf einen Blick — annehmen können Sie direkt auf dieser Seite.`)}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mt-10 text-sm text-[#EADFD2]">
            {data.datum && (
              <span className="inline-flex items-center gap-2" data-testid="angebot-datum">
                <Clock size={14} className="text-[#D99A5B]" />
                {data.termin_text || data.datum_lang}{data.uhrzeit ? ` · ${data.uhrzeit} Uhr` : ""}
              </span>
            )}
            {data.ort && <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-[#D99A5B]" />{data.ort}</span>}
            {s.personen > 0 && <span className="inline-flex items-center gap-2"><Users size={14} className="text-[#D99A5B]" />{s.personen} Personen</span>}
            {istStunden && (
              <span className="inline-flex items-center gap-2" data-testid="angebot-stundensatz-chip">
                <Users size={14} className="text-[#D99A5B]" />{EUR(s.stundensatz_netto)} netto / Stunde
              </span>
            )}
            {istTagessatz && (
              <span className="inline-flex items-center gap-2" data-testid="angebot-tagessatz-chip">
                <Users size={14} className="text-[#D99A5B]" />
                {data.kundentyp === "privat"
                  ? `${EUR(s.tagessatz_brutto)} inkl. MwSt / Tag`
                  : `${EUR(s.tagessatz_netto)} netto / Tag`} · bis {stundenText} Std.
              </span>
            )}
          </div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A392] mt-8">
            Angebot {data.nummer}
            {data.gueltig_bis ? ` · gültig bis ${data.gueltig_bis_de}` : ""}
          </p>
        </div>
      </section>

      {/* Menü */}
      {(data.gaenge || []).length > 0 && (
        <section className="bg-[#FDF8F1]" data-testid="angebot-menu">
          <div className="max-w-2xl mx-auto px-5 sm:px-6 py-16 md:py-20">
            <div className="relative bg-white border border-[#D9C3A6] shadow-[0_26px_60px_-30px_rgba(120,80,48,0.6)] px-6 sm:px-12 py-14 sm:py-16">
              <span className="absolute top-4 left-4 w-5 h-5 border-t border-l border-[#BA6D52]/60" />
              <span className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#BA6D52]/60" />
              <span className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-[#BA6D52]/60" />
              <span className="absolute bottom-4 right-4 w-5 h-5 border-b border-r border-[#BA6D52]/60" />
              <div className="text-center">
                <p className="text-xs sm:text-sm tracking-[0.4em] text-[#5E5147] font-medium">L U K A S&nbsp;&nbsp;S T U T Z</p>
                <Ornament className="mt-5" />
                <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mt-5 text-[#241F1A]">Menüvorschlag</h2>
                {data.untertitel && <p className="text-base sm:text-lg text-[#4A3F36] italic mt-4">{data.untertitel}</p>}
                {data.anlass && <p className="text-xs uppercase tracking-[0.3em] text-[#9C4F31] font-medium mt-5">{data.anlass}</p>}
                <Ornament className="mt-8" />
              </div>
              <div className="mt-10 space-y-9">
                {data.gaenge.map((g, i) => (
                  <div key={i} className="text-center" data-testid={`angebot-gang-${i}`}>
                    {g.label && <p className="text-[11px] uppercase tracking-[0.28em] text-[#9C4F31] font-medium">{g.label}</p>}
                    <p className="font-serif text-2xl md:text-3xl mt-3 leading-snug text-[#241F1A]">{g.titel}</p>
                    {g.beschreibung && (
                      <p className="text-base text-[#4A3F36] mt-2.5 font-normal italic leading-relaxed">{g.beschreibung}</p>
                    )}
                    {g.kunden_notiz && (
                      <p className="text-sm text-[#4A3F36] mt-3 bg-[#FAF5F1] border border-[#E3CDB7] px-3 py-2 inline-block"
                        data-testid={`angebot-gang-${i}-offener-wunsch`}>
                        Ihre Anmerkung: „{g.kunden_notiz}" — ich melde mich mit einem angepassten Vorschlag.
                      </p>
                    )}
                    {wuenscheMoeglich && (
                      <div className="mt-4">
                        {notizen[i] === undefined ? (
                          <button onClick={() => setNotizen((n) => ({ ...n, [i]: "" }))}
                            data-testid={`angebot-wunsch-oeffnen-${i}`}
                            className="text-xs uppercase tracking-[0.18em] text-[#8A5A42] underline underline-offset-4 decoration-[#D9BBA6] hover:text-[#BA6D52] transition-colors">
                            + Anmerkung zu diesem Gang
                          </button>
                        ) : (
                          <textarea rows={2} autoFocus value={notizen[i]}
                            onChange={(ev) => setNotizen((n) => ({ ...n, [i]: ev.target.value }))}
                            placeholder="z. B. „Klingt gut — statt Lachs lieber Rinderfilet?“"
                            data-testid={`angebot-wunsch-${i}`}
                            className="w-full bg-[#FFFDF8] border border-[#E8DCCB] focus:outline-none focus:border-[#BA6D52] px-4 py-3 text-sm text-left transition-colors" />
                        )}
                      </div>
                    )}
                    {i < data.gaenge.length - 1 && (
                      <div className="flex items-center justify-center gap-3 mt-9">
                        <span className="w-10 h-px bg-[#D9C7AE]" />
                        <span className="w-1.5 h-1.5 rotate-45 bg-[#BA6D52]/70" />
                        <span className="w-10 h-px bg-[#D9C7AE]" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Leistungen + Preis */}
      <section className="bg-[#F7EADA] border-y border-[#EBD9C2]">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20 grid grid-cols-1 md:grid-cols-5 gap-10">
          {(data.leistungen || []).length > 0 && (
            <div className="md:col-span-3" data-testid="angebot-leistungen">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#9C4F31]">Im Preis enthalten</p>
              <h2 className="font-serif text-2xl md:text-3xl font-light mt-4">Leistungen</h2>
              <ul className="mt-6 space-y-3">
                {data.leistungen.map((l, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-[#3C332C]" data-testid={`angebot-leistung-${i}`}>
                    <Check size={16} className="text-[#BA6D52] shrink-0 mt-0.5" /> {l}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={(data.leistungen || []).length > 0 ? "md:col-span-2" : "md:col-span-5"}>
            <div className="bg-[#FFFDF8] border border-[#E6D8C2] p-6" data-testid="angebot-preis">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#BA6D52]">
                {istStunden ? "Mein Stundensatz" : istTagessatz ? "Mein Tagessatz" : "Ihr Preis"}
              </p>
              {istTagessatz ? (
                <>
                  <p className="font-serif text-4xl mt-4 tabular-nums" data-testid="angebot-tagessatz">
                    {EUR(data.kundentyp === "privat" ? s.tagessatz_brutto : s.tagessatz_netto)}
                  </p>
                  <p className="text-sm text-[#4A3F36] mt-1">
                    {data.kundentyp === "privat"
                      ? `pro Einsatztag, inkl. ${s.mwst_prozent} % MwSt`
                      : `pro Einsatztag, netto · zzgl. ${s.mwst_prozent} % MwSt`}
                  </p>
                  <div className="mt-6 pt-5 border-t border-[#EBDFCB] space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">Enthaltene Stunden</span>
                      <span className="text-right">bis {stundenText} Std. pro Tag</span>
                    </div>
                    {data.kundentyp === "privat" ? (
                      <>
                        <div className="flex justify-between gap-3" data-testid="angebot-tage">
                          <span className="text-[#5E5147]">
                            {s.tage} Einsatztag{s.tage !== 1 ? "e" : ""}
                          </span>
                          <span className="tabular-nums whitespace-nowrap">
                            {EUR(s.tage * s.tagessatz_brutto)}
                          </span>
                        </div>
                        {s.anfahrt_netto > 0 && (
                          <div className="flex justify-between gap-3" data-testid="angebot-anfahrt">
                            <span className="text-[#5E5147]">
                              Anfahrtspauschale
                              {s.anfahrt_km > 0 ? ` (ca. ${Math.round(s.anfahrt_km)} km)` : ""}
                            </span>
                            <span className="tabular-nums whitespace-nowrap">{EUR(s.anfahrt_brutto)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-3">
                          <span className="text-[#5E5147]">enthaltene MwSt {s.mwst_prozent} %</span>
                          <span className="tabular-nums">{EUR(s.mwst_betrag)}</span>
                        </div>
                        <div className="flex justify-between gap-3 pt-3 border-t border-[#EBDFCB] font-medium">
                          <span>Gesamtbetrag</span>
                          <span className="tabular-nums whitespace-nowrap" data-testid="angebot-summe-brutto">{EUR(s.brutto)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between gap-3" data-testid="angebot-tage">
                          <span className="text-[#5E5147]">
                            {s.tage} Einsatztag{s.tage !== 1 ? "e" : ""} · netto
                          </span>
                          <span className="tabular-nums whitespace-nowrap">{EUR(s.tage_netto)}</span>
                        </div>
                        {s.anfahrt_netto > 0 && (
                          <div className="flex justify-between gap-3" data-testid="angebot-anfahrt">
                            <span className="text-[#5E5147]">
                              Anfahrtspauschale netto
                              {s.anfahrt_km > 0 ? ` (ca. ${Math.round(s.anfahrt_km)} km)` : ""}
                            </span>
                            <span className="tabular-nums whitespace-nowrap">{EUR(s.anfahrt_netto)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-3 pt-3 border-t border-[#EBDFCB]">
                          <span className="text-[#5E5147]">Summe netto</span>
                          <span className="tabular-nums" data-testid="angebot-summe-netto">{EUR(s.netto)}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-[#5E5147]">zzgl. MwSt {s.mwst_prozent} %</span>
                          <span className="tabular-nums">{EUR(s.mwst_betrag)}</span>
                        </div>
                        <div className="flex justify-between gap-3 pt-3 border-t border-[#EBDFCB] font-medium">
                          <span>Gesamtbetrag brutto</span>
                          <span className="tabular-nums whitespace-nowrap" data-testid="angebot-summe-brutto">{EUR(s.brutto)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : istStunden ? (
                <>
                  <p className="font-serif text-4xl mt-4 tabular-nums" data-testid="angebot-stundensatz">
                    {EUR(s.stundensatz_netto)}
                  </p>
                  <p className="text-sm text-[#4A3F36] mt-1">
                    pro Stunde, netto · zzgl. {s.mwst_prozent} % MwSt
                  </p>
                  <div className="mt-6 pt-5 border-t border-[#EBDFCB] space-y-2 text-sm">
                    {s.anfahrt_netto > 0 && (
                      <div className="flex justify-between gap-3" data-testid="angebot-anfahrt">
                        <span className="text-[#5E5147]">
                          Anfahrtspauschale netto
                          {s.anfahrt_km > 0 ? ` (ca. ${Math.round(s.anfahrt_km)} km)` : ""}
                        </span>
                        <span className="tabular-nums whitespace-nowrap">{EUR(s.anfahrt_netto)}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">Abrechnung</span>
                      <span className="text-right">nach geleisteten Stunden</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">Einsatz</span>
                      <span className="text-right">{data.termin_text || data.datum_lang}</span>
                    </div>
                  </div>
                </>
              ) : data.kundentyp === "privat" ? (
                <>
                  <p className="font-serif text-4xl mt-4 tabular-nums" data-testid="angebot-preis-pp">
                    {EUR(s.pro_person_brutto)}
                  </p>
                  <p className="text-sm text-[#4A3F36] mt-1">
                    pro Person, inkl. {s.mwst_prozent} % MwSt
                  </p>
                  <div className="mt-6 pt-5 border-t border-[#EBDFCB] space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">{s.personen} Personen</span>
                      <span className="tabular-nums">{EUR(s.positionen_brutto)}</span>
                    </div>
                    {s.anfahrt_netto > 0 && (
                      <div className="flex justify-between gap-3" data-testid="angebot-fahrtkosten">
                        <span className="text-[#5E5147]">
                          Fahrtkosten{s.anfahrt_km > 0 ? ` (ca. ${s.anfahrt_km} km)` : ""}
                        </span>
                        <span className="tabular-nums">{EUR(s.anfahrt_brutto)}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">Summe netto</span>
                      <span className="tabular-nums" data-testid="angebot-summe-netto">{EUR(s.netto)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">enthaltene MwSt {s.mwst_prozent} %</span>
                      <span className="tabular-nums">{EUR(s.mwst_betrag)}</span>
                    </div>
                    <div className="flex justify-between gap-3 pt-3 border-t border-[#EBDFCB] font-medium">
                      <span>Gesamtbetrag</span>
                      <span className="tabular-nums" data-testid="angebot-summe-brutto">{EUR(s.brutto)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-serif text-4xl mt-4 tabular-nums" data-testid="angebot-preis-pp">
                    {EUR(s.pro_person_netto)}
                  </p>
                  <p className="text-sm text-[#4A3F36] mt-1">pro Person, netto</p>
                  <div className="mt-6 pt-5 border-t border-[#EBDFCB] space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">{s.personen} Personen</span>
                      <span className="tabular-nums" data-testid="angebot-summe-netto">{EUR(s.positionen_netto)}</span>
                    </div>
                    {s.anfahrt_netto > 0 && (
                      <div className="flex justify-between gap-3" data-testid="angebot-fahrtkosten">
                        <span className="text-[#5E5147]">
                          Fahrtkosten netto{s.anfahrt_km > 0 ? ` (ca. ${s.anfahrt_km} km)` : ""}
                        </span>
                        <span className="tabular-nums">{EUR(s.anfahrt_netto)}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">Summe netto</span>
                      <span className="tabular-nums">{EUR(s.netto)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#5E5147]">zzgl. {s.mwst_prozent} % MwSt</span>
                      <span className="tabular-nums">{EUR(s.mwst_betrag)}</span>
                    </div>
                    <div className="flex justify-between gap-3 pt-3 border-t border-[#EBDFCB] font-medium">
                      <span>Gesamt brutto</span>
                      <span className="tabular-nums" data-testid="angebot-summe-brutto">{EUR(s.brutto)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Rahmenbedingungen */}
      {data.bedingungen && (
        <section className="max-w-3xl mx-auto px-6 py-14" data-testid="angebot-bedingungen">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#BA6D52]">Rahmenbedingungen</p>
          <p className="text-[15px] text-[#3C332C] mt-4 leading-relaxed whitespace-pre-line">
            {data.bedingungen}
          </p>
        </section>
      )}

      {/* Entscheidung */}
      <section className="bg-[#FFFDF8] border-y border-[#EFE3D2]">
        <div className="max-w-xl mx-auto px-6 py-16" data-testid="angebot-entscheidung">
          {data.status === "angenommen" ? (
            <div className="relative border border-[#E3DECE] bg-[#FFFDF8] px-6 py-12 text-center overflow-hidden animate-in fade-in zoom-in-95 duration-500"
              data-testid="angebot-angenommen">
              <span className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#BA6D52]/60" />
              <span className="absolute top-4 right-4 w-6 h-6 border-t border-r border-[#BA6D52]/60" />
              <span className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[#BA6D52]/60" />
              <span className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#BA6D52]/60" />

              <div className="relative mx-auto w-24 h-24 flex items-center justify-center animate-in zoom-in-50 duration-700">
                <span className="absolute inset-0 rounded-full border border-[#BA6D52]/40" />
                <span className="absolute inset-2 rounded-full border border-[#BA6D52]/20" />
                <img src="/logo.png" alt="" className="w-16 h-16 object-contain" />
              </div>

              <p className="text-[11px] uppercase tracking-[0.3em] text-[#BA6D52] mt-7">
                Auftrag bestätigt
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl font-light mt-4">
                Vielen Dank für Ihre Bestätigung
              </h2>
              <p className="text-[15px] text-[#3C332C] mt-5 font-light leading-relaxed max-w-md mx-auto">
                {data.angenommen_von ? `${data.angenommen_von}, ` : ""}ich freue mich sehr auf
                {s.tage > 1 && data.termin_text
                  ? ` Ihre Einsatztage ${data.termin_text}`
                  : data.datum ? ` den ${data.datum.split("-").reverse().join(".")}` : " Ihren Abend"} —
                {s.tage > 1 ? " alle Termine sind" : " der Termin ist"} fest für Sie eingeplant. Ihre Auftragsbestätigung können Sie hier
                direkt herunterladen; eine Kopie ist auch per E-Mail unterwegs.
              </p>

              <Ornament className="mt-8" />

              <a href={`${API}/angebotsseiten/${slug}/bestaetigung`}
                data-testid="angebot-download"
                className="mt-8 inline-flex items-center justify-center gap-2.5 bg-[#BA6D52] hover:bg-[#9C4F31] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors">
                <Download size={15} /> Auftragsbestätigung herunterladen
              </a>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-xl mx-auto">
                {[
                  ["Termin reserviert", data.termin_text || data.datum_lang || "wie abgestimmt"],
                  istStunden
                    ? ["Stundensatz", `${EUR(s.stundensatz_netto)} netto / Stunde`]
                    : istTagessatz
                    ? ["Tagessatz", `${EUR(s.tagessatz_netto)} netto / Tag · bis ${stundenText} Std.`]
                    : ["Menü & Personen", `${s.personen} Gäste, wie aufgeführt`],
                  ["Vor dem Termin", "ich melde mich wegen der letzten Details"],
                ].map(([titel, text], i) => (
                  <div key={i} className="border-t border-[#EBDFCB] pt-3">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#BA6D52]">{titel}</p>
                    <p className="text-xs text-[#4A3F36] mt-1.5 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>

              <p className="text-xs text-[#4A3F36] mt-9">
                Angebot {data.nummer}
                {data.angenommen_am
                  ? ` · bestätigt am ${new Date(data.angenommen_am).toLocaleString("de-DE", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit" })} Uhr`
                  : ""}
              </p>
            </div>
          ) : data.status === "abgelehnt" ? (
            <div className="bg-[#FBF1EF] border border-[#E8CFC8] px-6 py-10 text-center"
              data-testid="angebot-abgelehnt">
              <X className="text-[#A6543F] mx-auto" size={28} />
              <p className="font-serif text-2xl mt-4">Angebot abgelehnt</p>
              <p className="text-[15px] text-[#3C332C] mt-3 font-light">
                Danke für die Rückmeldung. Wenn es doch noch passt, rufen Sie einfach an.
              </p>
            </div>
          ) : data.abgelaufen ? (
            <div className="bg-[#FAF5F1] border border-[#E8DCCB] px-6 py-10 text-center"
              data-testid="angebot-abgelaufen">
              <FileText className="text-[#BA6D52] mx-auto" size={26} />
              <p className="font-serif text-2xl mt-4">Angebot abgelaufen</p>
              <p className="text-[15px] text-[#3C332C] mt-3 font-light">
                Die Gültigkeit endete am {data.gueltig_bis_de}. Melden Sie sich kurz — ich aktualisiere
                das Angebot gerne.
              </p>
            </div>
          ) : (
            <>
              {data.status === "rueckmeldung" && (
                <div className="mb-10 border border-[#E8DCCB] bg-[#FAF5F1] px-6 py-6 text-center"
                  data-testid="angebot-rueckmeldung-hinweis">
                  <MessageCircle className="text-[#BA6D52] mx-auto" size={22} />
                  <p className="font-serif text-2xl mt-3">Ihre Rückmeldung ist bei uns</p>
                  <p className="text-[15px] text-[#3C332C] mt-3 font-light leading-relaxed">
                    Vielen Dank für Ihre Anmerkungen{data.rueckmeldung_von ? `, ${data.rueckmeldung_von}` : ""}.
                    Ich passe das Menü an und schicke Ihnen zeitnah das überarbeitete Angebot — Sie können
                    das aktuelle Angebot aber auch jederzeit direkt annehmen.
                  </p>
                </div>
              )}
              <div className="text-center">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#BA6D52]">Ihre Entscheidung</p>
                <h2 className="font-serif text-3xl font-light mt-4">Passt das so für Sie?</h2>
                <p className="text-sm text-[#4A3F36] mt-4 font-light leading-relaxed">
                  Mit „Angebot annehmen" wird der Termin verbindlich für Sie reserviert. Name und
                  Funktion genügen — eine handschriftliche Unterschrift ist nicht nötig, die
                  elektronische Bestätigung ist gültig und wird protokolliert.
                  {wuenscheMoeglich && (
                    <> Wenn etwas anders sein soll: schreiben Sie Ihre Anmerkung direkt unter den
                    jeweiligen Gang und schicken Sie mir Ihre Änderungswünsche.</>
                  )}
                </p>
              </div>

              {modus === "annehmen" ? (
                <div className="mt-10 space-y-5" data-testid="angebot-annehmen-form">
                  <div>
                    <label className={LBL}>Ihr Name *</label>
                    <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="z. B. Romana Jarna" className={FIELD} data-testid="angebot-name" />
                  </div>
                  <div>
                    <label className={LBL}>Funktion (optional)</label>
                    <input value={form.funktion} onChange={(e) => setForm((f) => ({ ...f, funktion: e.target.value }))}
                      placeholder="z. B. Geschäftsführerin" className={FIELD} data-testid="angebot-funktion" />
                  </div>
                  <button onClick={annehmen} disabled={busy} data-testid="angebot-annehmen-senden"
                    className="w-full bg-[#BA6D52] hover:bg-[#9C4F31] disabled:opacity-60 text-white px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                    Verbindlich annehmen
                  </button>
                  <button onClick={() => setModus("")} data-testid="angebot-annehmen-abbrechen"
                    className="w-full text-[11px] uppercase tracking-[0.2em] text-[#6E5B4C] hover:text-[#BA6D52]">
                    Zurück
                  </button>
                </div>
              ) : modus === "ablehnen" ? (
                <div className="mt-10 space-y-5" data-testid="angebot-ablehnen-form">
                  <div>
                    <label className={LBL}>Grund (optional)</label>
                    <textarea rows={3} value={grund} onChange={(e) => setGrund(e.target.value)}
                      placeholder="z. B. Termin passt leider nicht" className={FIELD}
                      data-testid="angebot-ablehnen-grund" />
                  </div>
                  <button onClick={ablehnen} disabled={busy} data-testid="angebot-ablehnen-senden"
                    className="w-full border border-[#C9B9A6] text-[#4A3F36] hover:bg-[#F7EADA] px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />}
                    Absage senden
                  </button>
                  <button onClick={() => setModus("")} data-testid="angebot-ablehnen-abbrechen"
                    className="w-full text-[11px] uppercase tracking-[0.2em] text-[#6E5B4C] hover:text-[#BA6D52]">
                    Zurück
                  </button>
                </div>
              ) : modus === "wunsch" ? (
                <div className="mt-10 space-y-5" data-testid="angebot-wunsch-form">
                  <p className="text-sm text-[#4A3F36] font-light leading-relaxed">
                    Ihre Anmerkungen zu den einzelnen Gängen schreiben Sie oben direkt unter den
                    jeweiligen Gang. Hier können Sie noch etwas Allgemeines ergänzen.
                  </p>
                  <div>
                    <label className={LBL}>Ihr Name</label>
                    <input value={wunschName} onChange={(e) => setWunschName(e.target.value)}
                      placeholder="z. B. Romana Jarna" className={FIELD} data-testid="angebot-wunsch-name" />
                  </div>
                  <div>
                    <label className={LBL}>Allgemeine Anmerkung (optional)</label>
                    <textarea rows={3} value={allgemein} onChange={(e) => setAllgemein(e.target.value)}
                      placeholder="z. B. zwei Gäste vegetarisch, Beginn lieber 19 Uhr"
                      className={FIELD} data-testid="angebot-wunsch-allgemein" />
                  </div>
                  <button onClick={wuenscheSenden} disabled={busy} data-testid="angebot-wunsch-senden"
                    className="w-full border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                    Änderungswünsche senden
                  </button>
                  <button onClick={() => setModus("")} data-testid="angebot-wunsch-abbrechen"
                    className="w-full text-[11px] uppercase tracking-[0.2em] text-[#6E5B4C] hover:text-[#BA6D52]">
                    Zurück
                  </button>
                </div>
              ) : (
                <div className="mt-10 flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={() => setModus("annehmen")} data-testid="angebot-annehmen"
                      className="flex-1 bg-[#BA6D52] hover:bg-[#9C4F31] text-white px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                      <Check size={15} /> Angebot annehmen
                    </button>
                    <button onClick={() => setModus("ablehnen")} data-testid="angebot-ablehnen"
                      className="flex-1 border border-[#D8C9B6] text-[#4A3F36] hover:bg-[#F7EADA] px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                      <X size={15} /> Absagen
                    </button>
                  </div>
                  {wuenscheMoeglich && (
                    <button onClick={() => setModus("wunsch")} data-testid="angebot-wunsch"
                      className="border border-[#BA6D52]/50 text-[#BA6D52] hover:bg-[#FAF5F1] px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                      <MessageCircle size={15} /> Änderungswünsche senden
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Kontakt */}
      <section className="relative overflow-hidden text-[#FAF3E8]">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/event-bg-gericht.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,13,10,0.72)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-light">Noch eine Frage zum Angebot?</h2>
          <p className="text-sm text-[#A89D91] mt-3 font-light">
            Rufen Sie an oder schreiben Sie kurz — Änderungen sind jederzeit möglich.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href={`https://wa.me/${data.phone_intl}?text=${encodeURIComponent(`Guten Tag Herr Stutz, eine Frage zum Angebot ${data.nummer}:`)}`}
              target="_blank" rel="noreferrer" data-testid="angebot-whatsapp"
              className="inline-flex items-center gap-2 border border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/20 px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a href={`tel:+${data.phone_intl}`} data-testid="angebot-call"
              className="inline-flex items-center gap-2 border border-[#4A433B] text-[#FAF3E8] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              <Phone size={15} /> Anrufen
            </a>
          </div>
          <p className="text-xs text-[#5A5248] mt-12">
            Lukas Stutz · Mietkoch für Gastronomie &amp; Events · stutz-cuisine.com
          </p>
        </div>
      </section>
    </div>
  );
}
